import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import { SupplierPaymentService } from '../../services/supplierPayment.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { SupplierPayment, PaymentEditDTO } from '../../interfaces/procurement/supplierPayment.interface';
import { Account } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';

export const SupplierPaymentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [payment, setPayment] = useState<SupplierPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);

  // Editable fields
  const [amount, setAmount] = useState<number | ''>('' );
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');
  const [editReason, setEditReason] = useState('');

  // Load asset accounts (same mechanism as SupplierPaymentCreate)
  useEffect(() => {
    AccountService.getAccountsByType('ASSET')
      .then((accounts: Account[]) => setAssetAccounts(accounts))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load payment and prefill form
  useEffect(() => {
    if (!id) return;
    SupplierPaymentService.getById(id)
      .then(p => {
        setPayment(p);
        // Prefill all editable fields
        const rawAmount = p.amount;
        setAmount(Number.isFinite(rawAmount) ? rawAmount : '');
        setPaymentDate(p.paymentDate);
        setPaymentAccountId(p.paymentAccountId);
        setPaymentMethod(p.paymentMethod ?? '');
        setReference(p.reference ?? '');
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier payment'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!payment) return;
    if (editReason.trim().length === 0) {
      toast.showApiError(null, 'A reason is required');
      return;
    }
    const numericAmount = typeof amount === 'number' ? amount : NaN;
    if (!(numericAmount > 0)) {
      toast.showApiError(null, 'Amount must be a positive number');
      return;
    }
    setSaving(true);
    try {
      const dto: PaymentEditDTO = {
        amount: numericAmount,
        paymentDate,
        paymentAccountId,
        paymentMethod: paymentMethod || undefined,
        reference: reference || undefined,
        editReason: editReason.trim(),
      };
      const updated = await SupplierPaymentService.edit(payment.id, dto);
      toast.showSuccess('Payment updated');
      navigate(`/procurement/supplier-payments/${updated.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!payment) return <Box sx={{ p: 3 }}><Typography>Supplier payment not found.</Typography></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Edit Supplier Payment — {payment.paymentNumber}</Typography>

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Payment Date"
            type="date"
            value={paymentDate}
            onChange={e => setPaymentDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={e => {
              const v = e.target.value;
              setAmount(v === '' ? '' : Number(v));
            }}
            error={!(typeof amount === 'number' && amount > 0)}
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
            required
          />

          <AccountAutocomplete
            label="Payment Account"
            accounts={assetAccounts}
            value={paymentAccountId}
            onChange={setPaymentAccountId}
            required
          />

          <TextField
            label="Payment Method (optional)"
            value={paymentMethod}
            onChange={e => setPaymentMethod(e.target.value)}
            fullWidth
          />

          <TextField
            label="Reference (optional)"
            value={reference}
            onChange={e => setReference(e.target.value)}
            fullWidth
          />

          <TextField
            label="Reason for edit"
            value={editReason}
            onChange={e => setEditReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required
            error={editReason.trim().length === 0 && editReason.length > 0}
            helperText="Required — explain why this payment is being edited"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/procurement/supplier-payments/${payment.id}`)}
              disabled={saving}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SupplierPaymentEdit;
